import { useState, useCallback } from 'react';
import { Sparkles, ExternalLink, RotateCcw, Loader2 } from 'lucide-react';
import { useAiForgePopup, type AiForgeAssetPayload } from '@/hooks/useAiForgePopup';
import api from '@/lib/api';
import { useAiBudget, describeBudget } from '@/hooks/useAiBudget';

export type ImageStudioContext = 'product' | 'blog' | 'newsletter' | 'homepage';

export interface AiForgeButtonProps {
  isOpen: boolean;
  onClose: () => void;
  context: ImageStudioContext;
  contextName?: string;
  referenceImages?: { id?: string; url: string; thumbnail_url?: string; alt_text?: string }[];
  onImageGenerated: (image: { url: string; thumbnail_url?: string; alt_text?: string }) => void;
  initialPrompt?: string;
}

export default function AiForgeButton({
  isOpen,
  onClose,
  context,
  contextName,
  onImageGenerated,
  initialPrompt,
}: AiForgeButtonProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAssetReceived = useCallback(async (payload: AiForgeAssetPayload) => {
    setIsUploading(true);
    setError(null);

    try {
      let result: { url: string; thumbnail_url?: string };

      if (payload.dataUrl) {
        // Small asset — convert data URL to blob and upload as multipart
        const response = await fetch(payload.dataUrl);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append('file', blob, `ai-forge-${payload.assetType}.${getExtension(payload.mimeType)}`);
        formData.append('context', context);
        formData.append('asset_type', payload.assetType);

        result = await api.upload('/admin/ai/upload-asset', formData);
      } else if (payload.downloadUrl) {
        // Large asset — send download URL to backend for server-to-server fetch
        result = await api.post('/admin/ai/upload-asset', {
          download_url: payload.downloadUrl,
          mime_type: payload.mimeType,
          context,
          asset_type: payload.assetType,
        });
      } else {
        throw new Error('No asset data received');
      }

      onImageGenerated({
        url: result.url,
        thumbnail_url: result.thumbnail_url,
        alt_text: payload.metadata.prompt,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload asset');
    } finally {
      setIsUploading(false);
    }
  }, [context, onImageGenerated, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const { openForge, isOpen: popupIsOpen } = useAiForgePopup({
    context,
    suggestedPrompt: initialPrompt,
    allowedTypes: ['image', 'video'],
    onAssetReceived: handleAssetReceived,
    onClose: handleClose,
  });

  // Auto-open when isOpen prop becomes true
  // Only fetched while the dialog is open, so a page full of image fields does
  // not fire one request per field.
  const { data: budget } = useAiBudget(isOpen);
  const budgetLine = describeBudget(budget);

  if (isOpen && !popupIsOpen && !isUploading) {
    // Schedule for next tick to avoid state updates during render
    setTimeout(() => openForge(), 0);
  }

  if (!isOpen && !isUploading) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
        {isUploading ? (
          <div className="text-center space-y-3">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="font-medium">Uploading asset...</p>
            <p className="text-sm text-gray-500">Processing and saving to storage</p>
          </div>
        ) : error ? (
          <div className="text-center space-y-3">
            <p className="text-red-500 font-medium">Upload failed</p>
            <p className="text-sm text-gray-500">{error}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => { setError(null); openForge(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center space-y-3">
            <Sparkles className="w-8 h-8 mx-auto text-primary" />
            <p className="font-medium">
              AI Forge is open{contextName ? ` for ${contextName}` : ''}
            </p>
            <p className="text-sm text-gray-500">
              Generate your asset in the popup window, then click "Use This" to send it back here.
            </p>
            {/* Shown at the point of spend. The balance otherwise lived only on
                the Settings page, so whoever was generating could not see what
                it cost or what was left. */}
            {budgetLine && (
              <p className="text-xs text-gray-400 tabular-nums">{budgetLine}</p>
            )}
            <div className="flex gap-2 justify-center">
              <button
                onClick={openForge}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {popupIsOpen ? 'Focus Popup' : 'Reopen Popup'}
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getExtension(mimeType: string): string {
  const map: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/ogg': 'ogg',
    'audio/flac': 'flac',
  };
  return map[mimeType] || 'bin';
}
